"use client";

import { useCallback, useEffect, useRef } from "react";

/**
 * Cloudflare Turnstile integration — privacy-friendly captcha that
 * fronts visitor-auth + prechat-submit when the operator turns it on.
 *
 * Lifecycle:
 *   1. The hook lazy-loads Cloudflare's Turnstile script
 *      (`challenges.cloudflare.com/turnstile/v0/api.js`) on first
 *      `getToken()` call. Subsequent calls reuse the loaded script.
 *   2. `getToken()` renders an invisible challenge into a hidden
 *      DOM container and resolves with the token Cloudflare returns.
 *   3. Tokens are single-use + ~5-minute TTL. Each call to `getToken()`
 *      gets a fresh widget (Cloudflare also exposes an "execute" /
 *      "reset" API but instantiating fresh is simpler + more robust
 *      against widget-state desync).
 *
 * Disabled mode: when `siteKey` is null / undefined, `getToken()`
 * resolves to `undefined` immediately. Callers can treat the return
 * value as the `turnstileToken` to forward to the BE — `undefined`
 * means "send nothing" which the BE accepts when bot-protection is
 * off for the integration.
 *
 * SSR-safe: nothing touches `window` outside of the `useCallback`
 * body, which only runs in the browser via a real `getToken()` call.
 */

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SCRIPT_ID = "wexio-turnstile-script";

// Cloudflare's global injected by their script — typed minimally
// (we only ever call `render`, `reset`, `remove`).
interface TurnstileGlobal {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback?: (token: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
      "timeout-callback"?: () => void;
      execution?: "render" | "execute";
      appearance?: "always" | "execute" | "interaction-only";
      retry?: "auto" | "never";
      "refresh-expired"?: "auto" | "manual" | "never";
      size?: "normal" | "compact" | "flexible";
    },
  ) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
    /** Cloudflare's onload hook — we install it as a one-shot resolver
     *  so concurrent `getToken()` calls share the same script-load
     *  Promise. */
    __wexioTurnstileReady?: () => void;
  }
}

/** Shared promise — the first caller starts the script load, all
 *  concurrent + subsequent callers await the same Promise. */
let scriptLoadPromise: Promise<void> | null = null;

export function loadTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("turnstile: no window"));
  }
  if (window.turnstile) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise<void>((resolve, reject) => {
    // Re-use any existing tag (operator's host page may have loaded
    // Turnstile already for their own captcha). The `?render=explicit`
    // param is harmless if duplicated.
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      if (window.turnstile) return resolve();
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("turnstile: script load error")), {
        once: true,
      });
      return;
    }
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener(
      "error",
      () => {
        // A failed <script> never fires load/error again. Leaving the dead tag in place made the
        // retry take the `existing` branch above and wait forever on it.
        script.remove();
        reject(new Error("turnstile: script load error"));
      },
      { once: true },
    );
    document.head.appendChild(script);
  }).catch((err) => {
    // Clear so a follow-up call can retry — a flaky network shouldn't
    // permanently brick auth.
    scriptLoadPromise = null;
    throw err;
  });
  return scriptLoadPromise;
}

export interface UseTurnstileOptions {
  /** Cloudflare Turnstile site key (the operator's public identifier).
   *  `null` / `undefined` ⇒ Turnstile disabled; `getToken()` resolves
   *  to `undefined` without loading the script. */
  siteKey: string | null | undefined;
  /** Optional theme hint — defaults to `auto` (Cloudflare picks based
   *  on system theme). The visitor never sees the widget in invisible
   *  mode unless Cloudflare's risk-engine decides to challenge them,
   *  so this is mostly cosmetic for the visible fallback. */
  theme?: "light" | "dark" | "auto";
  /** Which side of the viewport the challenge container pins to,
   *  matching the launcher position so a visible challenge appears
   *  directly above the FAB instead of floating elsewhere on screen.
   *  Defaults to `"right"`. */
  position?: "left" | "right";
}

export interface UseTurnstileResult {
  /** Resolve a single-use Turnstile token. Resolves to `undefined`
   *  when Turnstile is disabled (no site key). Rejects on script load
   *  failure, render error, expiry, or visitor cancellation.
   *
   *  Call this immediately before a protected mutation. The token is
   *  one-shot — get a fresh one for every request. */
  getToken: () => Promise<string | undefined>;
}

/**
 * Tiny invisible-by-default Cloudflare Turnstile wrapper.
 *
 * Pass the BE-provided site key. Call `getToken()` right before each
 * BE mutation that needs a token; the hook handles script loading,
 * widget rendering, and cleanup.
 */
export function useTurnstile(opts: UseTurnstileOptions): UseTurnstileResult {
  const { siteKey, theme, position = "right" } = opts;
  // Hidden host container — created lazily, lives until unmount.
  // Cloudflare's widget mounts INSIDE this container; we render new
  // widget instances into the same container per `getToken()` call.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const themeRef = useRef(theme);
  useEffect(() => {
    themeRef.current = theme;
  }, [theme]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    // Visible-but-empty host container. With `appearance:
    // interaction-only` Cloudflare keeps the widget invisible 99% of
    // the time, but when the risk engine decides a visible challenge
    // IS needed (or the operator's site key is the "always forces
    // interactive" test key `3x00000000000000000000FF`), the iframe
    // expands inside this container. If we hid the container with
    // `width:0; height:0; overflow:hidden; pointer-events:none`
    // (silent-mode setup), the challenge would render but stay
    // clipped + uninteractive — the visitor's tap would go straight
    // through to the launcher behind it, and `getToken()` would hang
    // forever waiting on a callback that can't fire.
    //
    // Pinned just above the launcher on the same side (the launcher
    // sits at `bottom: 16px`, height 56px → top at 72px; we use
    // 88px so a typical 65px-tall challenge sits with a small gap
    // above the FAB). Above the launcher (z:50) and the auth-error
    // popup (z:40) so a forced challenge isn't occluded by either.
    const el = document.createElement("div");
    el.style.position = "fixed";
    el.style.bottom = "88px";
    if (position === "left") {
      el.style.left = "16px";
    } else {
      el.style.right = "16px";
    }
    el.style.zIndex = "60";
    document.body.appendChild(el);
    containerRef.current = el;
    return () => {
      el.remove();
      containerRef.current = null;
    };
  }, [position]);

  const getToken = useCallback(async (): Promise<string | undefined> => {
    if (!siteKey) return undefined;
    await loadTurnstileScript();
    const container = containerRef.current;
    const turnstile = typeof window !== "undefined" ? window.turnstile : undefined;
    if (!container || !turnstile) {
      throw new Error("turnstile: not ready");
    }
    return new Promise<string>((resolve, reject) => {
      // `appearance: "interaction-only"` keeps the challenge hidden
      // unless Cloudflare's risk engine decides a visible challenge
      // is warranted (suspicious traffic) — typical case is silent.
      const widgetId = turnstile.render(container, {
        sitekey: siteKey,
        // Invisible-by-default UX is now driven by `appearance:
        // interaction-only` alone — Cloudflare deprecated
        // `size: "invisible"` (rejects the parameter outright). The
        // widget stays hidden + 0×0 until the risk engine decides a
        // visible challenge is needed, at which point it expands
        // into our visible container.
        execution: "render",
        appearance: "interaction-only",
        retry: "auto",
        "refresh-expired": "auto",
        callback: (token: string) => {
          resolve(token);
          // Remove this widget instance so a stale token can't sneak
          // back via Cloudflare's auto-refresh. Next `getToken()` call
          // will render fresh.
          try {
            turnstile.remove(widgetId);
          } catch {
            /* widget already removed by reset; ignore */
          }
        },
        "error-callback": () => {
          try {
            turnstile.remove(widgetId);
          } catch {
            /* ignore */
          }
          reject(new Error("turnstile: error"));
        },
        "expired-callback": () => {
          try {
            turnstile.remove(widgetId);
          } catch {
            /* ignore */
          }
          reject(new Error("turnstile: expired before submission"));
        },
        "timeout-callback": () => {
          try {
            turnstile.remove(widgetId);
          } catch {
            /* ignore */
          }
          reject(new Error("turnstile: timeout"));
        },
      });
    });
  }, [siteKey]);

  return { getToken };
}
